import {
  Body,
  Heading,
  Icon,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Tr,
  VStack,
  Divider,
  Box,
} from '@hover/blueprint';
import { iCheck, iX } from '@hover/icons';
import React, { useState } from 'react';
import { nanoid } from 'nanoid';
import {
  IPricingFeature,
  IPricingSection,
} from '../../../../interfaces/IPricingPageTableData';
import { PRICING_TABLE_DATA } from '../../../../constants';
import HeaderCards from './HeaderCards';
import PricingTooltip from '../../plans-and-pricing/PricingTooltip';
import { useInterSectionObserver } from '../../../../hooks';

const TableData = PRICING_TABLE_DATA.map((section: IPricingSection, index) => (
  <Tbody borderTop="none" key={section.sectionName}>
    <Tr borderBottomWidth="1px" borderColor="neutral.400">
      <Td py={0} style={{ paddingLeft: 0 }}>
        <Heading
          mt={index === 0 ? 0 : 800}
          marginBottom={500}
          size={500}
          fontWeight={700}
        >
          {section.sectionName}
        </Heading>
      </Td>
    </Tr>
    {section.features.map((feature: IPricingFeature) => (
      <Tr
        borderBottomWidth="1px"
        borderColor="neutral.400"
        key={feature.feature}
      >
        <SimpleGrid columns={6} spacing={600}>
          <Td d="flex" minW="322px" maxH={500} alignItems="center">
            <PricingTooltip
              minW="390px"
              pr={400}
              pl={{ base: 0, tablet: 600 }}
              tooltip={{
                id: nanoid(),
                title: feature.feature,
                tooltip: feature.tooltip,
              }}
            />
          </Td>
          <Td />
          {feature.values.map(value => (
            <Td key={nanoid()} maxH={500} d="flex" alignItems="center">
              {typeof value === 'string' ? (
                <Body textAlign="center" mx="auto" size={400}>
                  {value}
                </Body>
              ) : (
                <Icon
                  icon={value ? iCheck : iX}
                  size="default"
                  mx="auto"
                  color={value ? 'success.600' : 'brandOrange.300'}
                />
              )}
            </Td>
          ))}
        </SimpleGrid>
      </Tr>
    ))}
  </Tbody>
));

const DesktopTable: React.FC = () => {
  const [headerVisible, setHeaderVisible] = useState<boolean>(false);

  const headerRef = useInterSectionObserver(
    isIntersecting => {
      setHeaderVisible(isIntersecting);
    },
    {
      rootMargin: '0px',
      threshold: 1,
      root: null,
    },
  );

  return (
    <Box
      flexDir="column"
      d={{ base: 'none', desktop: 'flex' }}
      alignItems="center"
      position="relative"
    >
      <Box
        ref={headerRef.elementRef}
        h="80px"
        zIndex={200}
        w="3px"
        position="absolute"
        top="-80px"
      />
      <Box
        px={500}
        flexDir="column"
        position="sticky"
        top="90px"
        bg="brandTan.100"
        overflow="hidden"
        zIndex={100}
        pt={500}
        shadow={!headerVisible ? 'distance300' : 'none'}
        w="100%"
      >
        <SimpleGrid
          alignSelf="center"
          maxWidth="1248px"
          mb={500}
          columns={2}
          w="100%"
          spacing={600}
        >
          <Box alignItems="center" justifyContent="flex-end">
            <VStack
              spacing={400}
              maxW="30%"
              w="100%"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Body size={600} isBold color="neutral.300">
                Pay as you go
              </Body>
              <Divider borderColor="neutral.400" />
            </VStack>
          </Box>
          <Box alignItems="center" justifyContent="center">
            <VStack spacing={400} w="100%">
              <Body size={600} isBold color="neutral.300">
                Membership
              </Body>
              <Divider borderColor="neutral.400" />
            </VStack>
          </Box>
        </SimpleGrid>
        <Box maxW="1248px" alignSelf="center" alignItems="center">
          <HeaderCards headerVisible={headerVisible} />
        </Box>
      </Box>
      <Box px={500} pt={400}>
        <Table bg="brandTan.100" w="100%" maxW="1248px">
          {TableData}
        </Table>
      </Box>
    </Box>
  );
};

export default DesktopTable;
